import { ImportTableStateType } from '.';
import CloneUtils from '../../../../../../../utils/cloneUtils';
import ObjCheck from '../../../../../../../utils/objCheck';
import Utils from '../../../../../../../utils/utils';
import {
    DescriptorsMapType,
    DescriptorsToLabelsMapType,
    DescriptorsToScalesMapType,
    DescriptorsToTypesMapType,
    ImportDescriptorDataTypeType,
    ImportTableDescriptorType,
    ImportTableReportType,
    ReportsMapType,
} from '../../../../types';
import { NUMBER_DESCRIPTOR_DATA_TYPE } from '../../constants';

/**
 * Creates a mapping of descriptor IDs to their labels
 * @param descriptors Array of import table descriptor objects
 * @returns Object mapping descriptor IDs to their label strings
 */
export function getDescriptorsToLabels(descriptors: ImportTableDescriptorType[]): DescriptorsToLabelsMapType {
    return descriptors.reduce((acc: DescriptorsToLabelsMapType, curr) => {
        acc[curr.id] = curr.label;
        return acc;
    }, {});
}

/**
 * Creates a mapping of descriptor IDs to their scale configurations
 * Performs a shallow copy of the scale object to prevent reference issues
 * @param descriptors Array of import table descriptor objects
 * @returns Object mapping descriptor IDs to their scale configurations
 */
export function getDescriptorsToScales(descriptors: ImportTableDescriptorType[]): DescriptorsToScalesMapType {
    return descriptors.reduce((acc: DescriptorsToScalesMapType, curr) => {
        acc[curr.id] = curr.inputScale.value;
        return acc;
    }, {});
}

/**
 * Creates a mapping of descriptor IDs to their data types
 * @param descriptors Array of import table descriptor objects
 * @returns Object mapping descriptor IDs to their data type strings
 */
export function getDescriptorsToTypes(descriptors: ImportTableDescriptorType[]): DescriptorsToTypesMapType {
    return descriptors.reduce((acc: DescriptorsToTypesMapType, curr) => {
        acc[curr.id] = curr.dataType;
        return acc;
    }, {});
}

/**
 * Converts an array of descriptor objects into a map keyed by descriptor ID
 * @param descriptors Array of import table descriptor objects
 * @returns Object mapping descriptor IDs to their complete descriptor objects
 */
export function getDescriptorsMap(descriptors: ImportTableDescriptorType[]): DescriptorsMapType {
    return descriptors.reduce((acc: DescriptorsMapType, curr) => {
        acc[curr.id] = curr;
        return acc;
    }, {});
}

/**
 * Creates the initial state for the descriptors store
 * Performs a deep clone of the input descriptors to prevent mutations
 * @param initialDescriptors Array of import table descriptor objects
 * @returns Initial state object for the store
 */
export function getDescriptorsInitialState(initialDescriptors: ImportTableDescriptorType[]) {
    const descriptors: ImportTableDescriptorType[] = CloneUtils.deepClone(initialDescriptors);
    return {
        descriptorsMap: getDescriptorsMap(descriptors),
        initialDescriptorsToLabels: getDescriptorsToLabels(descriptors),
        initialDescriptorsToScales: getDescriptorsToScales(descriptors),
        initialDescriptorsToTypes: getDescriptorsToTypes(descriptors),
        labelEditing: false,
        typeEditing: false,
        scaleEditing: false,
    };
}

/**
 * Converts an array of report objects into a map keyed by report ID
 * @param initialReports Array of import table report objects
 * @param options Configuration object for creation
 * @returns Object mapping report IDs to their complete report objects
 */
export function getReportsState(
    initialReports: ImportTableReportType[],
    options: { editing: boolean } = { editing: false }
) {
    const reports: ImportTableReportType[] = CloneUtils.deepClone(initialReports);
    const reportsMap = reports.reduce((acc: ReportsMapType, curr) => {
        acc[curr.id] = { ...curr, editing: options.editing };
        return acc;
    }, {});

    return {
        reportsMap,
        initialReportsMap: CloneUtils.deepClone(reportsMap),
    };
}

export function getImportTableStoreState(
    initialDescriptors: ImportTableDescriptorType[],
    initialReports: ImportTableReportType[]
): ImportTableStateType {
    return {
        ...getDescriptorsInitialState(initialDescriptors),
        ...getReportsState(initialReports),
        reportsErrors: {},
        activeCoordinates: {
            activeRow: null,
            activeCol: null,
            maxRow: initialDescriptors.length - 1,
            maxCol: initialReports.length - 1,
        },
    };
}

export function validateValue(descriptor: ImportTableDescriptorType, fieldValue: any) {
    const errors = { error: false, message: '' };

    if (ObjCheck.isNullUndefinedEmptyOrDash(fieldValue)) {
        return errors;
    }

    if (descriptor.dataType === NUMBER_DESCRIPTOR_DATA_TYPE && !Utils.isNumber(fieldValue)) {
        errors.error = true;
        errors.message = 'Invalid Number Value';
    }

    return errors;
}

export function getIsScalable(descriptorDataType: ImportDescriptorDataTypeType, value: any): boolean {
    return descriptorDataType === NUMBER_DESCRIPTOR_DATA_TYPE && value !== null;
}
